import React, { useEffect, useMemo, useState } from 'react';
import { ProductLogo, NexelusEndorsement } from '../branding/Branding';
import useBranding from '../branding/useBranding';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import ListAltOutlined from '@mui/icons-material/ListAltOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import CreditCardOutlined from '@mui/icons-material/CreditCardOutlined';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import AltRouteOutlined from '@mui/icons-material/AltRouteOutlined';
import ApartmentOutlined from '@mui/icons-material/ApartmentOutlined';
import PowerOutlined from '@mui/icons-material/PowerOutlined';
import BarChartOutlined from '@mui/icons-material/BarChartOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import ViewSidebarOutlined from '@mui/icons-material/ViewSidebarOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import TopMenu from '../../components/TopMenu/TopMenu';
import { httpGet } from '../../HttpRequestHandling/httpRequestHandler';
import { UpdateFavorites } from '../../store/action/favoritesAction';
import { checkForUnAuthorizedException, getDisplayName } from '../../components/Common/Utilities';
import theme from '../theme';
import { buildNav, crumbsFor, moduleTarget, moduleTitle, sectionOf } from './navModel';
import { PageLabels } from '../editor/labelText';
import { A11yNames } from './a11y';
import { ensureFonts } from '../fonts';
import SearchIcon from '@mui/icons-material/Search';
import CommandPalette from './CommandPalette';
import OverviewVideoDialog from './OverviewVideoDialog';
import PlayCircleOutline from '@mui/icons-material/PlayCircleOutline';
import '../redesign.css';

const ICONS = {
	dashboard: DashboardOutlined, invoices: ListAltOutlined, archive: Inventory2Outlined, payments: CreditCardOutlined,
	models: LayersOutlined, master: TuneOutlined, users: PeopleOutlined, roles: LockOutlined, rules: AltRouteOutlined,
	company: ApartmentOutlined, integrations: PowerOutlined, reports: BarChartOutlined
};
const COLLAPSE_KEY = 'apworks.nav.collapsed';

// Same display-name rules as the classic left menu (LeftMenu/MenuItems.js)
const relabel = item => {
	if (item.id === 'VendorPayToMapping') return `Vendor ${getDisplayName('site')} Mapping`;
	if (item.id === 'UserInvoiceStatuses') return getDisplayName('User Status');
	return null;
};

const readCollapsed = () => {
	try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
};

function NavItem({ item, onNavigate }) {
	const Icon = ICONS[item.icon];
	return (
		<NavLink to={item.path} end className={({ isActive }) => 'apw2-item' + (isActive ? ' active' : '')} title={item.label} onClick={onNavigate}>
			{Icon ? <Icon fontSize="inherit" /> : null}<span>{item.label}</span>
		</NavLink>
	);
}

function NewShell({ menu, saveMenu, tokenHandler, appEnvironment, children }) {
	useBranding();
	const location = useLocation();
	const navigate = useNavigate();
	const [collapsed, setCollapsed] = useState(readCollapsed);
	const [navOpen, setNavOpen] = useState(false);
	const [openGroups, setOpenGroups] = useState({});
	const [paletteOpen, setPaletteOpen] = useState(false);
	const [videoOpen, setVideoOpen] = useState(false);

	// Load the menu the same way the classic LeftMenu does; the server applies the user's rights.
	useEffect(() => {
		httpGet('/api/layout/GetAllMenu')
			.then(response => saveMenu(response.data.leftMenu))
			.catch(error => { if (error && error.response) checkForUnAuthorizedException(null, error); });
	}, [saveMenu]);

	// Body class for the new look, and the new UI's fonts (loaded only in the new UI).
	useEffect(() => {
		document.body.classList.add('apw2-on');
		ensureFonts();
		return () => document.body.classList.remove('apw2-on');
	}, []);

	useEffect(() => { setNavOpen(false); }, [location.pathname]);

	// ⌘K / Ctrl+K opens the command palette, except over the invoice editor (leaving it must go through its Close).
	useEffect(() => {
		const onKey = e => {
			if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K') && !document.querySelector('.apw2-ed')) {
				e.preventDefault();
				setPaletteOpen(true);
			}
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, []);

	const nav = useMemo(() => buildNav(menu, location.pathname, relabel), [menu, location.pathname]);
	const crumbs = crumbsFor(nav, location.pathname);
	const pageTitle = moduleTitle(nav, location.pathname);
	const path = location.pathname.toLowerCase().replace(/\/+$/, '');

	const toggleCollapsed = () => {
		const next = !collapsed;
		setCollapsed(next);
		try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* per-browser convenience only */ }
	};
	const groupIsOpen = g => openGroups[g.id] != null ? openGroups[g.id] : g.items.some(i => i.path.toLowerCase().replace(/\/+$/, '') === path);

	return (
		<ThemeProvider theme={theme}>
			<div className={'apw2' + (collapsed ? ' apw2-collapsed' : '') + (navOpen ? ' apw2-nav-open' : '')}>
				<aside className="apw2-nav" aria-label="Main navigation">
					<Link className="apw2-logo" to={nav.current ? nav.current.base + '/Dashboard/' : '/'}><ProductLogo /><img className="apworks-nav-symbol" src="/images/apworks-symbol.svg" alt="APWorks" /></Link>
					{nav.modules.length > 1 && (
						<div className="apw2-modules" role="tablist" aria-label="Invoice type">
							{nav.modules.map(m => (
								<button key={m.id} type="button" role="tab" aria-selected={nav.current && nav.current.id === m.id} title={`${m.label} invoices`}
									onClick={() => navigate(moduleTarget(menu, location.pathname, m))}>{collapsed ? m.label[0] : m.label}</button>
							))}
						</div>
					)}
					<nav className="apw2-scroll">
						{nav.sections.map(s => (
							<div key={s.title}>
								<div className="apw2-sec">{s.title}</div>
								{s.items.map(i => <NavItem key={i.id} item={i} onNavigate={() => setNavOpen(false)} />)}
								{s.group && (() => {
									const g = s.group;const open = groupIsOpen(g);const Icon = ICONS[g.icon];
									return (
										<React.Fragment>
											<button type="button" className={'apw2-item' + (g.items.some(i => i.path.toLowerCase().replace(/\/+$/, '') === path) ? ' active' : '')} aria-expanded={open} title={g.label}
												onClick={() => {
													// A collapsed sidebar has no room for a group's items: expand it and open the group.
													if (collapsed) { toggleCollapsed(); setOpenGroups(o => ({ ...o, [g.id]: true })); return; }
													setOpenGroups(o => ({ ...o, [g.id]: !open }));
												}}>
												{Icon ? <Icon fontSize="inherit" /> : null}<span>{g.label}</span><ExpandMore className="apw2-chev" />
											</button>
											{open && <div className="apw2-sub">{g.items.map(i => <NavItem key={i.id} item={i} onNavigate={() => setNavOpen(false)} />)}</div>}
										</React.Fragment>
									);
								})()}
							</div>
						))}
					</nav>
					<div className="apw2-foot"><NexelusEndorsement />{appEnvironment ? <span className="apw2-env">{appEnvironment}</span> : null}</div>
				</aside>
				{navOpen && <div className="apw2-scrim" onClick={() => setNavOpen(false)} />}
				<div className="apw2-frame">
					<div className="apw2-top" role="banner">
						<button type="button" className="apw2-icon-btn apw2-only-mobile" aria-label="Open menu" onClick={() => setNavOpen(true)}><MenuIcon fontSize="small" /></button>
						<button type="button" className="apw2-icon-btn apw2-hide-mobile" aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} onClick={toggleCollapsed}><ViewSidebarOutlined fontSize="small" /></button>
						<div className="apw2-crumbs">
							{crumbs.map((c, i) => (
								<React.Fragment key={i}>
									{i > 0 && <span className="sep">/</span>}
									{c.path && i < crumbs.length - 1 ? <Link to={c.path}>{c.label}</Link> : <span className={i === crumbs.length - 1 ? 'cur' : ''}>{c.label}</span>}
								</React.Fragment>
							))}
						</div>
						<span className="apw2-grow" />
						<button type="button" className="apw2-search-btn" onClick={() => setPaletteOpen(true)} aria-label="Search (Ctrl+K)" title="Search (Ctrl+K)">
							<SearchIcon fontSize="small" /><span className="apw2-hide-mobile">Search</span><kbd className="apw2-hide-mobile">{/Mac/i.test(navigator.platform || '') ? '⌘K' : 'Ctrl K'}</kbd>
						</button>
						<button type="button" className="apw2-search-btn" onClick={() => setVideoOpen(true)} aria-label="Watch the new UI overview video" title="Watch the new UI overview video">
							<PlayCircleOutline fontSize="small" /><span className="apw2-hide-mobile">Overview video</span>
						</button>
						<TopMenu tokenHandler={tokenHandler} variant="new" />
					</div>
					<div className="apw2-body" data-shell-title={pageTitle ? '' : undefined} data-section={sectionOf(location.pathname)}>
						{pageTitle && <div className="apw2-page-title"><h1>{pageTitle.title}</h1><span className="apw2-mod-chip">{pageTitle.module}</span></div>}
						{children}
					</div>
				</div>
			</div>
			<CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} nav={nav} />
			<OverviewVideoDialog open={videoOpen} onClose={() => setVideoOpen(false)} />
			<PageLabels />
			<A11yNames />
		</ThemeProvider>
	);
}

const mapStateToProps = state => ({ menu: state.favoriteReducer.favorites });
const mapDispatchToProps = dispatch => ({ saveMenu: value => dispatch(UpdateFavorites(value)) });

export default connect(mapStateToProps, mapDispatchToProps)(NewShell);
